// Contracts — vista shared enums (2026-08-10).
//
// Every vocabulary here is OURS. Unlike a mirror-driven project there is no
// upstream API echoing values at us: the client (Lightly) has none, verdicts
// arrive by manual reconciliation, and clips are created by our own ingest.
// That is why these are strict enums rather than loose strings — an unknown
// value is a bug on our side, and it should throw.
import { z } from 'zod';

// ── people ──────────────────────────────────────────────────────────────────

// Privilege order (low → high), "highest role wins" scoping — same shape as
// kids. viewer reads its team, admin also grows it, manager spans all teams.
export const VistaRoleSchema = z.enum(['viewer', 'admin', 'manager']);
export type VistaRole = z.infer<typeof VistaRoleSchema>;

export const VISTA_ROLE_RANK: Record<VistaRole, number> = {
  viewer: 0,
  admin: 1,
  manager: 2,
};

/** True if `role` clears a gate requiring `target`. */
export const vistaAllows = (role: VistaRole | undefined, target: VistaRole): boolean =>
  role !== undefined && VISTA_ROLE_RANK[role] >= VISTA_ROLE_RANK[target];

/** Managers see every contributor; everyone else is confined to their team. */
export const vistaSeesAllPools = (role: VistaRole | undefined): boolean => role === 'manager';

// invited — account created by an admin, person has not logged in yet
// joined  — logged in, nothing filmed yet
// active  — has filmed material
// blocked — we stopped working with them
export const VistaContributorStatusSchema = z.enum(['invited', 'joined', 'active', 'blocked']);
export type VistaContributorStatus = z.infer<typeof VistaContributorStatusSchema>;

// ── captures ────────────────────────────────────────────────────────────────

/**
 * The clip lifecycle. It is ONE axis of the captures row, never a side table.
 *
 *   recording       — multipart upload open, bytes still arriving
 *   filmed          — bytes complete, integrity gate passed
 *   needs_voiceover — queued for dubbing (narration is recorded off-platform)
 *   voiced          — dubbed master attached
 *   delivered       — handed to the client (manual; they have no API)
 *   pending         — awaiting the client's verdict
 *   accepted        — paid on THIS and nothing else
 *   rejected        — refused; the reason goes back to the contributor
 *   quarantine      — the recorder failed its own integrity gate, but the parts
 *                     already uploaded were SEALED instead of discarded: a long
 *                     take is evidence even when it is not material. Hidden from
 *                     the working stacks; staff can download and judge it.
 *
 * `filmed` / `delivered` deliberately match the kids vocabulary — the same fact
 * should not have a third name on the third project.
 */
export const VistaCaptureStatusSchema = z.enum([
  'recording',
  'filmed',
  'needs_voiceover',
  'voiced',
  'delivered',
  'pending',
  'accepted',
  'rejected',
  'quarantine',
]);
export type VistaCaptureStatus = z.infer<typeof VistaCaptureStatusSchema>;

/** Both are accepted by the client — unlike the reference product, which brands
 *  vertical footage as a defect. Stored so QA can filter, not to gate ingest. */
export const VistaOrientationSchema = z.enum(['landscape', 'portrait']);
export type VistaOrientation = z.infer<typeof VistaOrientationSchema>;

/**
 * Откуда взялся клип.
 *
 * 'recorder' — наш веб-рекордер: мы знаем число закодированных кадров, и гейт
 * целостности сравнивает их с таймером.
 * 'upload'   — человек снял системной камерой и загрузил готовый файл. Это
 * запасной путь на случай, если рекордер подведёт на конкретном телефоне;
 * кадры считать не по чему, поэтому целостность остаётся NULL, а качество
 * смотрит человек.
 */
export const VistaCaptureSourceSchema = z.enum(['recorder', 'upload']);
export type VistaCaptureSource = z.infer<typeof VistaCaptureSourceSchema>;

/** Client-facing topic priority: what they actively buy vs what they told us
 *  they do not want. Kept as data because the list is revised between waves. */
export const VistaTaskPrioritySchema = z.enum(['focus', 'deprioritised']);
export type VistaTaskPriority = z.infer<typeof VistaTaskPrioritySchema>;

// ── shared atoms ────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Contributor email — the identity key. Hours and money are attributed through
 *  it, so it is immutable in practice. Lowercased by the worker. */
export const VistaEmailSchema = z.string().trim().regex(EMAIL_RE).max(200);

/** Epoch ms, nullable: a clip that never finished has no upload time. */
export const VistaTsSchema = z.number().int().nullable();

/**
 * Money in cents. Never a float — payout arithmetic on fractional dollars
 * accumulates error across thousands of clips.
 */
export const VistaCentsSchema = z.number().int();
